import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiBatch, unwrapBatch } from '@/api/batch';
import type {
    AnalyticsGranularity,
    AggregatedValuesResponse,
    NetWorthProjectionResponse,
    ChartPeriod,
    BatchSubRequest,
} from '@/api/types';

interface UseBatchDashboardAnalyticsParams {
    /** User-selected granularity for chart display (M/Y toggle) */
    granularity: AnalyticsGranularity;
    /** Chart period for time range selection */
    period?: ChartPeriod;
    /** Optional override for projection return rate (decimal, e.g., 0.07 = 7%) */
    projectionReturnRate?: number | null;
    /** Optional override for projection annual contribution amount */
    projectionContribution?: number | null;
    /** Optional override for projection yearly debt change (negative = paydown) */
    projectionDebtChange?: number | null;
    /** Number of years to project into the future (uses server default if not specified) */
    projectionYears?: number;
    /** Whether FI mode is on; when false NO FI params are sent */
    projectionFiEnabled?: boolean;
    /** Optional annual expenses for FI target; null = no FI line */
    projectionAnnualExpenses?: number | null;
    /** Collection IDs counted toward FI; null/empty = all included (no param sent) */
    projectionFiCollectionIds?: number[] | null;
    /** FI multiplier (100 / withdrawal rate); undefined = server default */
    projectionFiMultiplier?: number;
    /** FI-specific override for the investable bucket return rate (decimal); null = auto-derived */
    projectionInvestableReturnRate?: number | null;
    /** Whether the post-FI withdrawal simulation is on; off = no new params sent */
    projectionSimulateWithdrawal?: boolean;
    /** Inflation-rate override for the real-terms simulation (decimal); null = server default */
    projectionInflationRate?: number | null;
}

/**
 * Batch response data returned by the hook.
 */
export interface BatchDashboardAnalyticsData {
    /** Assets aggregated values at user-selected granularity */
    assets: AggregatedValuesResponse;
    /** Debts aggregated values at user-selected granularity */
    debts: AggregatedValuesResponse;
    /** Net worth projection data - null when period is not 'future' */
    projection: NetWorthProjectionResponse | null;
}

/**
 * Build batch requests dynamically based on parameters.
 * - Fetches data at user-selected granularity (no separate monthly fetch)
 * - Adds projection when period === 'future'
 */
function buildBatchRequests(
    granularity: AnalyticsGranularity,
    includeFuture: boolean,
    projectionReturnRate?: number | null,
    projectionContribution?: number | null,
    projectionDebtChange?: number | null,
    projectionYears?: number,
    projectionFiEnabled?: boolean,
    projectionAnnualExpenses?: number | null,
    projectionFiCollectionIds?: number[] | null,
    projectionFiMultiplier?: number,
    projectionInvestableReturnRate?: number | null,
    projectionSimulateWithdrawal?: boolean,
    projectionInflationRate?: number | null
): BatchSubRequest[] {
    const requests: BatchSubRequest[] = [
        // Fetch at user-selected granularity only
        { id: 'assets', path: `/api/ledgers/analytics/aggregated-values?type=asset&granularity=${granularity}` },
        { id: 'debts', path: `/api/ledgers/analytics/aggregated-values?type=debt&granularity=${granularity}` },
    ];

    // Add projection when period is 'future'
    if (includeFuture) {
        // Build projection URL with optional overrides
        const params = new URLSearchParams();
        if (projectionReturnRate != null) {
            params.set('returnRate', projectionReturnRate.toString());
        }
        if (projectionContribution != null) {
            params.set('contribution', projectionContribution.toString());
        }
        if (projectionDebtChange != null) {
            params.set('debtChange', projectionDebtChange.toString());
        }
        if (projectionYears != null) {
            params.set('years', projectionYears.toString());
        }
        // FI params are sent only when FI mode is on; otherwise the projection
        // is a plain total-net-worth line with no investable series or target.
        if (projectionFiEnabled) {
            params.set('fiEnabled', '1');
            if (projectionAnnualExpenses != null) {
                params.set('annualExpenses', projectionAnnualExpenses.toString());
            }
            // Only send collections when a non-empty subset is selected (null/empty = all).
            if (projectionFiCollectionIds != null && projectionFiCollectionIds.length > 0) {
                params.set('collections', projectionFiCollectionIds.join(','));
                // The investable-rate override only has an effect in compositional
                // mode (FI on + real subset), so send it only alongside a collections
                // subset and only when the user has actually overridden the rate.
                if (projectionInvestableReturnRate != null) {
                    params.set('investableReturnRate', projectionInvestableReturnRate.toString());
                }
            }
            if (projectionFiMultiplier != null) {
                params.set('fiMultiplier', projectionFiMultiplier.toString());
            }
            // Post-FI withdrawal simulation. Sent only when the toggle is on AND
            // an FI target exists (annualExpenses set); toggle off => no new
            // params, keeping the request byte-identical to today.
            if (projectionSimulateWithdrawal && projectionAnnualExpenses != null) {
                params.set('simulateWithdrawal', '1');
                // The inflation override is sent only when the user set it;
                // otherwise the server falls back to its AppConfig default.
                if (projectionInflationRate != null) {
                    params.set('inflationRate', projectionInflationRate.toString());
                }
            }
        }
        const queryString = params.toString();
        const projectionPath = '/api/ledgers/analytics/net-worth-projection' + (queryString ? `?${queryString}` : '');
        requests.push({ id: 'projection', path: projectionPath });
    }

    return requests;
}

/**
 * Hook to fetch all dashboard analytics data in a single batch request.
 * This minimizes API calls and Lambda invocations.
 *
 * Combines:
 * - Aggregated values for assets (at user-selected granularity)
 * - Aggregated values for debts (at user-selected granularity)
 * - Net worth projection (when period='future')
 */
export function useBatchDashboardAnalytics({
    granularity,
    period = 'max',
    projectionReturnRate,
    projectionContribution,
    projectionDebtChange,
    projectionYears,
    projectionFiEnabled,
    projectionAnnualExpenses,
    projectionFiCollectionIds,
    projectionFiMultiplier,
    projectionInvestableReturnRate,
    projectionSimulateWithdrawal,
    projectionInflationRate,
}: UseBatchDashboardAnalyticsParams) {
    const includeFuture = period === 'future';

    return useQuery({
        queryKey: ['batch-dashboard-analytics', { granularity, period, projectionReturnRate, projectionContribution, projectionDebtChange, projectionYears, projectionFiEnabled, projectionAnnualExpenses, projectionFiCollectionIds, projectionFiMultiplier, projectionInvestableReturnRate, projectionSimulateWithdrawal, projectionInflationRate }],
        placeholderData: keepPreviousData,
        staleTime: 60000, // 60 seconds - matches existing analytics staleTime
        queryFn: async () => {
            const requests = buildBatchRequests(granularity, includeFuture, projectionReturnRate, projectionContribution, projectionDebtChange, projectionYears, projectionFiEnabled, projectionAnnualExpenses, projectionFiCollectionIds, projectionFiMultiplier, projectionInvestableReturnRate, projectionSimulateWithdrawal, projectionInflationRate);

            const result = await apiBatch<{
                assets: AggregatedValuesResponse;
                debts: AggregatedValuesResponse;
                projection?: NetWorthProjectionResponse;
            }>(requests);

            // Assets and debts are required to render the chart
            const assets = unwrapBatch(result.responses.assets, 'assets');
            const debts = unwrapBatch(result.responses.debts, 'debts');

            // A failed projection sub-request must not silently degrade
            // Future mode to historical-only data
            const projection = includeFuture
                ? unwrapBatch(result.responses.projection, 'projection')
                : null;

            return {
                assets,
                debts,
                projection,
            } as BatchDashboardAnalyticsData;
        },
    });
}
